import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAllergensFromApi,
  fetchSuppliersFromApi,
} from "../../utils/contentApi";
import AnimatedScroll from "../../components/AnimatedScroll/AnimatedScroll";
import "./allergens.css";

const ALLERGEN_COLORS = [
  "#FFD600", // Gluten
  "#C5E1A5", // Crustacés
  "#FFA726", // Œufs
  "#A5D6A7", // Poissons
  "#90CAF9", // Arachides
  "#FFCC80", // Soja
  "#FFF176", // Lait & Lactose
  "#CE93D8", // Fruits à Coques
  "#BDBDBD", // Céleri
  "#FF8A65", // Moutarde
  "#388E3C", // Graine de Sésames
  "#E0E0E0", // Anhydride & Sulfites
  "#FFAB91", // Lupin
  "#66BB6A", // Mollusques
];

function Allergens() {
  const navigate = useNavigate();
  const [dishes, setDishes] = useState([]);
  const [labels, setLabels] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDish, setSelectedDish] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [allergensResult, suppliersResult] = await Promise.all([
        fetchAllergensFromApi(),
        fetchSuppliersFromApi(),
      ]);

      setDishes(allergensResult.dishes);
      setLabels(allergensResult.labels);
      setSuppliers(suppliersResult);
    } catch {
      setError("Impossible de charger le tableau des allergènes.");
    } finally {
      setLoading(false);
    }
  }, []);

  const supplierRows =
    suppliers.length > 0
      ? suppliers
      : dishes
          .flatMap((dish) =>
            (Array.isArray(dish.suppliers) ? dish.suppliers : []).map(
              (name) => ({
                id: `fallback-${name}`,
                name,
                restaurant: "Carpe Diem",
              }),
            ),
          )
          .filter(
            (item, index, list) =>
              list.findIndex(
                (candidate) =>
                  String(candidate.name || "").toLowerCase() ===
                  String(item.name || "").toLowerCase(),
              ) === index,
          );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/menu");
  };

  if (loading) {
    return (
      <div className="allergens-page">
        <div className="allergens-loading">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="allergens-page">
        <div className="allergens-error">{error}</div>
      </div>
    );
  }

  return (
    <AnimatedScroll>
      <div className="allergens-page">
        <div className="allergens-header">
          <button
            type="button"
            className="allergens-back-button"
            onClick={handleBack}
            aria-label="Retour à la page précédente"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            Retour
          </button>
          <h1 className="allergens-title">Tableau des Allergènes</h1>
          <p className="allergens-subtitle">
            Retrouvez ci-dessous la liste des allergènes présents dans nos
            plats.
          </p>
          <p className="allergens-subtitle-note">
            Cliquez sur nos plats pour connaître son origine et ses
            fournisseurs.
          </p>
        </div>

        {dishes.length === 0 ? (
          <p className="allergens-empty">
            Le tableau des allergènes sera bientôt disponible.
          </p>
        ) : (
          <>
            <div className="allergens-scroll-hint">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Faites défiler horizontalement pour voir tous les allergènes
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </div>
            <div className="allergens-table-wrap">
              <table className="allergens-table">
                <thead>
                  <tr>
                    <th className="allergens-banner-corner" rowSpan={2}></th>
                    <th
                      className="allergens-banner-cell"
                      colSpan={labels.length}
                    >
                      Allergènes
                    </th>
                  </tr>
                  <tr>
                    {labels.map((label, colIdx) => (
                      <th
                        key={label}
                        className="allergens-col-header"
                        style={{
                          backgroundColor: ALLERGEN_COLORS[colIdx] || "#eee",
                        }}
                      >
                        <span className="allergens-col-text">{label}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="allergens-category-row">
                    <td
                      className="allergens-category-cell"
                      colSpan={labels.length + 1}
                    >
                      Plat de La Carte
                    </td>
                  </tr>
                  {dishes.map((dish) => (
                    <tr
                      key={dish.id}
                      className="allergens-dish-row"
                      onClick={() => setSelectedDish(dish)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="allergens-dish-name">{dish.name}</td>
                      {labels.map((label, colIdx) => {
                        const isActive = !!dish.allergens?.[label];
                        const lightColor = ALLERGEN_COLORS[colIdx]
                          ? ALLERGEN_COLORS[colIdx] + "40"
                          : "#eee";
                        return (
                          <td
                            key={label}
                            className={`allergens-cell${isActive ? " allergens-cell-active" : ""}`}
                            style={{
                              backgroundColor: isActive
                                ? ALLERGEN_COLORS[colIdx] || "#eee"
                                : lightColor,
                            }}
                          >
                            {isActive ? "✕" : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="allergens-meat-origin-section">
              <h3 className="allergens-meat-origin-title">Origine :</h3>
              <div className="allergens-meat-origin-wrap">
                <table className="allergens-meat-origin-table">
                  <thead>
                    <tr>
                      <th>Plat</th>
                      <th>Origine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dishes.map((dish) => (
                      <tr key={`origin-${dish.id}`}>
                        <td>{dish.name}</td>
                        <td>{dish.origin || "Non renseignée"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="allergens-partners-section">
              <h3 className="allergens-partners-title">
                Liste des fournisseurs :
              </h3>
              <div className="allergens-partners-wrap">
                {supplierRows.length ? (
                  <table className="allergens-partners-table">
                    <thead>
                      <tr>
                        <th>Fournisseur</th>
                        <th>Restaurant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierRows.map((row) => (
                        <tr key={row.id || row.name}>
                          <td>{row.name || "Non renseigné"}</td>
                          <td>{row.restaurant || "Carpe Diem"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="allergens-partners-empty">
                    Aucun fournisseur renseigné pour le moment.
                  </p>
                )}
              </div>
            </div>

            {selectedDish && (
              <div
                className="allergens-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="allergens-suppliers-title"
                onClick={() => setSelectedDish(null)}
              >
                <div
                  className="allergens-suppliers-section allergens-suppliers-modal"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className="allergens-modal-close"
                    aria-label="Fermer la popup"
                    onClick={() => setSelectedDish(null)}
                  >
                    ×
                  </button>

                  <h3
                    className="allergens-suppliers-title"
                    id="allergens-suppliers-title"
                  >
                    {selectedDish.name}
                  </h3>

                  {selectedDish.suppliers?.length ? (
                    <table className="allergens-suppliers-table">
                      <thead>
                        <tr>
                          <th>Fournisseur</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDish.suppliers.map((supplier) => (
                          <tr key={supplier}>
                            <td>{supplier}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="allergens-suppliers-empty">
                      Aucun fournisseur renseigné pour ce plat.
                    </p>
                  )}

                  <p className="allergens-suppliers-origin">
                    Origine : {selectedDish.origin || "Non renseignée"}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AnimatedScroll>
  );
}

export default Allergens;
